import {
  Box,
  Divider,
  Fade,
  IconButton,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import Typography from "common/components/Typography";
import RequestDialog from "common/components/RequestDialog";
import { formatTimer } from "common/utils";
import React, { useContext, useEffect, useState } from "react";
import PicksList from "./PicksList";
import { DraftContext, DraftProvider, DraftState } from "./context";
import { DraftPoolEntry, RoomContext } from "App/context";
import ActionButton from "./ActionButton";
import BoxList from "./BoxList";
import { useSocket } from "common/hooks/useSocket";
import { DraftCharacter } from "types/Draft/type";
import BansList from "./BansList";
import EndDraft from "./EndDraft";
import { useTranslation } from "react-i18next";
import { Room, RoomState } from "types/Room/type";
import LeaveDraft from "./ConfirmLeaveDraft";
import { useInputKeyEvent } from "common/hooks/useKonamiCode";
import PauseDraft from "./PauseDraft";
import { useNavigate } from "react-router-dom";
import { Logout, Undo } from "@mui/icons-material";
import { useActingRole } from "common/hooks/useActingRole";

type DraftProps = {};

type UndoRequest = {
  id: string;
  requester: "Player 1" | "Player 2";
  stepIndex: number;
};

const Draft = (props: DraftProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const socket = useSocket();
  const key = useInputKeyEvent();
  const {
    dispatch,
    stepIndex,
    currentStep,
    timerPlayer1,
    timerStatePlayer1,
    timerPlayer2,
    timerStatePlayer2,
    bansPlayer1,
    bansPlayer2,
    picksPlayer1,
    picksPlayer2,
    selectedBansPlayer1,
    selectedBansPlayer2,
    selectedPicksPlayer1,
    selectedPicksPlayer2,
    toBanPlayer1,
    toBanPlayer2,
  } = useContext(DraftContext);
  const {
    dispatch: dispatchRoom,
    role,
    player1,
    player2,
    player3,
    player4,
    draftModel,
    balancingBansPlayer1,
    jokerBansPlayer1,
    balancingBansPlayer2,
    jokerBansPlayer2,
    params,
  } = useContext(RoomContext);

  const [endDraft, setEndDraft] = useState<boolean>(false);
  const [showEndDraft, setShowEndDraft] = useState<boolean>(false);
  const [openLeave, setOpenLeave] = useState<boolean>(false);
  const [openPauseDraft, setOpenPauseDraft] = useState<boolean>(false);
  const [undoRequest, setUndoRequest] = useState<UndoRequest | null>(null);
  const theme = useTheme();
  // En solo, le joueur joue le camp dont c'est le tour.
  const actingRole = useActingRole();

  const isDraftPaused = (draft?: Partial<DraftState>) =>
    !!draft &&
    draft.currentStep !== undefined &&
    draft.timerStatePlayer1 === "Pause" &&
    draft.timerStatePlayer2 === "Pause";

  const subscribeEvents = () => {
    socket.on("update-room", (room: Room) => dispatchRoom(room));
    socket.on("room-state", (state: RoomState["state"]) => {
      if (state === "InWaitingRoom") navigate("/waitingRoom");
      else if (state === "EndDraft") setEndDraft(true);
    });
    socket.on("update-draft", (draft: DraftState) => {
      const nextDraft = {
        ...draft,
        selectedBansPlayer1: draft.selectedBansPlayer1 ?? undefined,
        selectedBansPlayer2: draft.selectedBansPlayer2 ?? undefined,
        selectedPicksPlayer1: draft.selectedPicksPlayer1 ?? undefined,
        selectedPicksPlayer2: draft.selectedPicksPlayer2 ?? undefined,
        toBanPlayer1: draft.toBanPlayer1 ?? undefined,
        toBanPlayer2: draft.toBanPlayer2 ?? undefined,
      };
      dispatch(nextDraft);
      setOpenPauseDraft(isDraftPaused(nextDraft));
    });
    socket.on("end-draft", () => {
      setEndDraft(true);
    });
    socket.on("pause-draft", () => setOpenPauseDraft(true));
    socket.on("resume-draft", () => setOpenPauseDraft(false));
    socket.on("undo-request", (request: UndoRequest | null) =>
      setUndoRequest(request),
    );
    // Les persos jouables et leur sélectionnabilité sont calculés par le serveur.
    socket.on("draft-pool", (draftPool: DraftPoolEntry[]) =>
      dispatchRoom({ draftPool: draftPool ?? [] }),
    );
  };

  const unselectCharacter = (
    character: DraftCharacter,
    list?: DraftCharacter[],
  ) => {
    const index = list?.findIndex((c) => c.name === character.name);
    if (index !== undefined && index !== -1) {
      list?.splice(index, 1);
    }
    return list;
  };

  useEffect(() => {
    subscribeEvents();

    socket.emit("get-draft");
    socket.emit("get-room-state");
    if (draftModel.steps.length === 0) socket.emit("get-room");

    return () => {
      socket.removeListener("update-room");
      socket.removeListener("room-state");
      socket.removeListener("update-draft");
      socket.removeListener("end-draft");
      socket.removeListener("pause-draft");
      socket.removeListener("resume-draft");
      socket.removeListener("draft-pool");
      socket.removeListener("undo-request");
    };
  }, []);

  const isCaptain = role === "Player 1" || role === "Player 2";
  const isSolo = params.solo === true;
  const previousStep =
    stepIndex !== undefined ? draftModel.steps[stepIndex - 1] : undefined;
  // En solo, l'annulation est immédiate et vaut pour l'étape précédente, quel qu'en soit le camp.
  const canUndoSolo =
    isSolo &&
    isCaptain &&
    stepIndex !== undefined &&
    stepIndex > 0 &&
    currentStep !== undefined;
  const canRequestUndo =
    canUndoSolo ||
    (!isSolo &&
      isCaptain &&
      stepIndex !== undefined &&
      stepIndex > 0 &&
      currentStep !== undefined &&
      currentStep.player !== role &&
      currentStep.player !== "Both" &&
      (previousStep?.player === role || previousStep?.player === "Both") &&
      undoRequest === null);
  const canRespondToUndo =
    undoRequest !== null &&
    isCaptain &&
    undoRequest.requester !== role &&
    currentStep?.player === role &&
    stepIndex === undoRequest.stepIndex;

  return (
    <Box height="100%" width="100%">
      <Fade
        in={!endDraft}
        timeout={400}
        onExited={() => setShowEndDraft(true)}
        unmountOnExit
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          gap={3}
          height="100%"
          sx={{
            background: ({ palette }) =>
              `radial-gradient(${
                currentStep?.action === "Ban"
                  ? palette.background.ban
                  : palette.common.player1.bgConstel
              }99, ${palette.background.default})`,
          }}
        >
          <LeaveDraft open={openLeave} setOpen={setOpenLeave} />
          <PauseDraft
            open={openPauseDraft && !endDraft && undoRequest === null}
          />
          <RequestDialog
            key={undoRequest?.id}
            open={undoRequest !== null}
            onAccept={
              canRespondToUndo
                ? () =>
                    undoRequest &&
                    socket.emit("send-undo-response", {
                      requestId: undoRequest.id,
                      accepted: true,
                    })
                : undefined
            }
            onDecline={
              canRespondToUndo
                ? () =>
                    undoRequest &&
                    socket.emit("send-undo-response", {
                      requestId: undoRequest.id,
                      accepted: false,
                    })
                : undefined
            }
            title={t("Annulation d'étape - {{player}}", {
              player:
                undoRequest?.requester === "Player 1"
                  ? player1?.pseudo
                  : player2?.pseudo,
            })}
            message={
              undoRequest ? (
                <Typography size="sm">
                  {t("{{player}} veut annuler sa dernière action.", {
                    player:
                      undoRequest.requester === "Player 1"
                        ? player1?.pseudo
                        : player2?.pseudo,
                  })}
                </Typography>
              ) : null
            }
          />
          <PicksList
            player={"player1"}
            playerName={player1?.pseudo ?? ""}
            teamMateNames={[player3?.pseudo]}
            picks={picksPlayer1}
            selected={selectedPicksPlayer1}
            balancingBans={balancingBansPlayer1}
            jokerBans={jokerBansPlayer1}
            onClick={
              currentStep?.action === "Pick" &&
              actingRole === "Player 1" &&
              (currentStep?.player === "Player 1" ||
                currentStep?.player === "Both")
                ? (character: DraftCharacter) =>
                    socket.emit(
                      "send-selected-chars-pick",
                      unselectCharacter(character, selectedPicksPlayer1),
                    )
                : undefined
            }
          />
          <Stack
            direction="column"
            height="100%"
            gap={2}
            width="100%"
            py="1rem"
          >
            <Stack direction="row" justifyContent="space-between">
              <BansList
                player="Player 1"
                color={theme.palette.common.player1.banCard}
                banSteps={draftModel.steps.filter(
                  (step) =>
                    step.action === "Ban" &&
                    (step.player === "Player 1" || step.player === "Both"),
                )}
                bans={bansPlayer1}
                selected={selectedBansPlayer1}
                hiddenBans={toBanPlayer1}
                hideBans={
                  currentStep?.action === "Ban" &&
                  actingRole === "Player 2" &&
                  currentStep?.player === "Both"
                }
                onClick={
                  currentStep?.action === "Ban" &&
                  actingRole === "Player 1" &&
                  (currentStep?.player === "Player 1" ||
                    currentStep?.player === "Both")
                    ? (character: DraftCharacter) =>
                        socket.emit(
                          "send-selected-chars-ban",
                          unselectCharacter(character, selectedBansPlayer1),
                        )
                    : undefined
                }
              />
              <Stack alignItems="center" width="100%">
                <Typography size="m">
                  {currentStep?.action === "Pick"
                    ? t("Phase de pick")
                    : currentStep?.action === "Ban"
                      ? t("Phase de ban")
                      : ""}
                </Typography>
                {/* timer: 0 = sans limite de temps (réglable en solo) : rien à afficher. */}
                {draftModel.timer > 0 && (
                  <Stack
                    width="100%"
                    direction="row"
                    justifyContent="space-evenly"
                    gap={2}
                  >
                    <Typography
                      size="m"
                      color={timerStatePlayer1 === "Play" ? "white" : "gray"}
                      textAlign="end"
                      width="100%"
                    >
                      {formatTimer(timerPlayer1)}
                    </Typography>
                    <Divider orientation="vertical" />
                    <Typography
                      size="m"
                      color={timerStatePlayer2 === "Play" ? "white" : "gray"}
                      textAlign="start"
                      width="100%"
                    >
                      {formatTimer(timerPlayer2)}
                    </Typography>
                  </Stack>
                )}
                <Stack direction="row" gap={1}>
                  {isCaptain && (
                    <Tooltip
                      title={
                        isSolo
                          ? t("Annuler la dernière action")
                          : t("Demander l'annulation de ta dernière action")
                      }
                      arrow
                    >
                      <span>
                        <IconButton
                          disabled={!canRequestUndo}
                          onClick={() => socket.emit("send-undo-request")}
                        >
                          <Undo />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <IconButton onClick={() => setOpenLeave(true)}>
                    <Logout />
                  </IconButton>
                </Stack>
              </Stack>
              <BansList
                player="Player 2"
                color={theme.palette.common.player2.banCard}
                banSteps={draftModel.steps.filter(
                  (step) =>
                    step.action === "Ban" &&
                    (step.player === "Player 2" || step.player === "Both"),
                )}
                bans={bansPlayer2}
                selected={selectedBansPlayer2}
                hiddenBans={toBanPlayer2}
                hideBans={
                  currentStep?.action === "Ban" &&
                  actingRole === "Player 1" &&
                  currentStep?.player === "Both"
                }
                onClick={
                  currentStep?.action === "Ban" &&
                  actingRole === "Player 2" &&
                  (currentStep?.player === "Player 2" ||
                    currentStep?.player === "Both")
                    ? (character: DraftCharacter) =>
                        socket.emit(
                          "send-selected-chars-ban",
                          unselectCharacter(character, selectedBansPlayer2),
                        )
                    : undefined
                }
              />
            </Stack>
            <BoxList />
            {(role == "Player 1" || role === "Player 2") && <ActionButton />}
          </Stack>
          <PicksList
            player={"player2"}
            playerName={player2?.pseudo ?? ""}
            teamMateNames={[player4?.pseudo]}
            picks={picksPlayer2}
            selected={selectedPicksPlayer2}
            balancingBans={balancingBansPlayer2}
            jokerBans={jokerBansPlayer2}
            onClick={
              currentStep?.action === "Pick" &&
              actingRole === "Player 2" &&
              (currentStep?.player === "Player 2" ||
                currentStep?.player === "Both")
                ? (character: DraftCharacter) =>
                    socket.emit(
                      "send-selected-chars-pick",
                      unselectCharacter(character, selectedPicksPlayer2),
                    )
                : undefined
            }
          />
        </Stack>
      </Fade>
      {showEndDraft && (
        <Fade in timeout={400}>
          <Box height="100%" width="100%">
            <EndDraft />
          </Box>
        </Fade>
      )}
    </Box>
  );
};

const DraftWithContext = () => {
  return (
    <DraftProvider>
      <Draft />
    </DraftProvider>
  );
};

export default DraftWithContext;
